import { getValidAuthToken } from '@services/auth/auth';
import { generateAntiBotToken, generateAntiBotTokenWithReload } from './recaptcha';
import { AntiBotUnavailableError } from './recaptcha-loader';
import { consumeChallengeToken, consumeChallengeTicket } from './antibot-challenge-stash';

const DEFAULT_ACTION = 'portal_integration';
const CHALLENGE_HEADER = 'X-Anti-Bot-Challenge';
const CHALLENGE_TICKET_HEADER = 'X-Anti-Bot-Challenge-Ticket';

export type PortalAntiBotHeaderOptions = {
  /** When true, force a full reCAPTCHA SDK reload before minting. */
  reload?: boolean;
  /** When true, attach the just-solved X-Anti-Bot-Challenge token (D1). */
  challenge?: true;
};

/**
 * Builds portal-integration request headers and always includes `X-Anti-Bot`.
 *
 * Callers must not swallow token failures — omitting the header causes the
 * backend to reject the request. Pass `{ reload: true }` after a 403
 * AntiBotInvalidTokenError so a spent single-use token is not reused, or
 * `{ challenge: true }` after the user solves a step-up challenge to attach
 * the solved token alongside a freshly minted `X-Anti-Bot` (D1 -- the score
 * check still runs normally; the challenge only ever promotes CHALLENGE to
 * ALLOW).
 *
 * @param action reCAPTCHA action label forwarded to token minting.
 * @param opts Optional reload/challenge flags for post-403/428 recovery.
 * @returns Headers with `X-Anti-Bot`, optional `X-Anti-Bot-Challenge`, and optional `Authorization`.
 * @throws {AntiBotUnavailableError} when the token cannot be minted or is empty.
 */
export async function buildPortalIntegrationHeaders(
  action: string = DEFAULT_ACTION,
  opts: PortalAntiBotHeaderOptions = {},
): Promise<Record<string, string>> {
  const authToken = await getValidAuthToken();
  const antiBotToken = await mintRequiredToken(action, opts.reload === true);
  const headers = assembleHeaders(authToken, antiBotToken);
  if (opts.challenge) {
    const challengeToken = consumeChallengeToken();
    if (challengeToken) headers[CHALLENGE_HEADER] = challengeToken;
    const challengeTicket = consumeChallengeTicket();
    if (challengeTicket) headers[CHALLENGE_TICKET_HEADER] = challengeTicket;
  }
  return headers;
}

/**
 * Mints a non-empty anti-bot token, optionally after reloading the SDK.
 */
async function mintRequiredToken(action: string, reload: boolean): Promise<string> {
  const antiBotToken = reload
    ? await generateAntiBotTokenWithReload(action)
    : await generateAntiBotToken(action);
  if (!antiBotToken) {
    throw new AntiBotUnavailableError(
      'Anti-bot token is required but was not generated',
    );
  }
  return antiBotToken;
}

/**
 * Assembles the final header map from optional auth and required anti-bot tokens.
 */
function assembleHeaders(
  authToken: string | null | undefined,
  antiBotToken: string,
): Record<string, string> {
  const headers: Record<string, string> = { 'X-Anti-Bot': antiBotToken };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;
  return headers;
}
